/**
 * useBountiesV2 - 重构后的赏金任务 hooks
 * 基于四阶段生命周期设计：投递期 → 开发期 → 审查期 → 结算(终态)
 * @see docs/grills/task-module-interaction-logic.md
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { getBrowserSupabaseClient } from "@/lib/supabase/client";
import { queryKeys } from "@/lib/query-client";
import type { Bounty, BountyStatus, DeliveryReview } from "@/lib/supabase/types";
import { getStatusLabel } from "@/domains/bounties/status-machine";

// ========== 任务大厅查询 ==========

export interface HallBountyItem {
  id: string;
  title: string;
  reward_usdc: number;
  bidding_deadline: string | null;
  completion_deadline: string | null;
  tech_tags: string[] | null;
  category: string | null;
  bid_count: number;
  status: BountyStatus;
  publisher_id: string | null;
  created_at: string;
}

interface UseHallBountiesOptions {
  category?: string;
  status?: BountyStatus;
  minReward?: number;
  maxReward?: number;
  search?: string;
  sort?: "default" | "reward" | "deadline" | "newest";
  page?: number;
  pageSize?: number;
}

export function useHallBountiesV2(options: UseHallBountiesOptions = {}) {
  const {
    category,
    status = "bidding",
    minReward,
    maxReward,
    search,
    sort = "default",
    page = 0,
    pageSize = 10,
  } = options;

  return useQuery({
    queryKey: queryKeys.bounties.hall({ category, search, sort, page }),
    queryFn: async (): Promise<{ items: HallBountyItem[]; total: number }> => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const from = page * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("bounties")
        .select(
          "id, title, reward_usdc, bidding_deadline, completion_deadline, tech_tags, category, bid_count, status, publisher_id, created_at",
          { count: "exact" }
        )
        .eq("status", status)
        .order("created_at", { ascending: false })
        .range(from, to);

      // 分类筛选
      if (category && category !== "全部") {
        query = query.eq("category", category);
      }

      // 赏金范围筛选
      if (minReward !== undefined) {
        query = query.gte("reward_usdc", minReward);
      }
      if (maxReward !== undefined) {
        query = query.lte("reward_usdc", maxReward);
      }

      // 搜索
      if (search?.trim()) {
        query = query.ilike("title", `%${search.trim()}%`);
      }

      // 排序
      if (sort === "reward") {
        query = query.order("reward_usdc", { ascending: false });
      } else if (sort === "deadline") {
        query = query.order("bidding_deadline", { ascending: true });
      }

      const { data, error, count } = await query;

      if (error) throw new Error(error.message);

      return {
        items: (data ?? []) as HallBountyItem[],
        total: count ?? 0,
      };
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000,
  });
}

// ========== 我发布的任务 ==========

export interface PublishedBountyItem {
  id: string;
  title: string;
  reward_usdc: number;
  status: BountyStatus;
  bidding_deadline: string | null;
  completion_deadline: string | null;
  bid_count: number;
  review_round: number;
  created_at: string;
}

interface UsePublishedBountiesOptions {
  publisherId: string | null;
  page?: number;
  pageSize?: number;
}

export function usePublishedBounties(options: UsePublishedBountiesOptions) {
  const { publisherId, page = 0, pageSize = 10 } = options;

  return useQuery({
    queryKey: [...queryKeys.bounties.my(publisherId ?? ""), "published", { page }],
    queryFn: async (): Promise<{ items: PublishedBountyItem[]; total: number }> => {
      if (!publisherId) return { items: [], total: 0 };

      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const from = page * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await supabase
        .from("bounties")
        .select(
          "id, title, reward_usdc, status, bidding_deadline, completion_deadline, bid_count, review_round, created_at",
          { count: "exact" }
        )
        .eq("publisher_id", publisherId)
        .order("updated_at", { ascending: false })
        .range(from, to);

      if (error) throw new Error(error.message);

      return {
        items: (data ?? []) as PublishedBountyItem[],
        total: count ?? 0,
      };
    },
    enabled: !!publisherId,
    staleTime: 30 * 1000,
  });
}

// ========== 我参与的任务（投标/中标） ==========

type BidSelectionStatus = "pending" | "selected" | "not_selected";

export interface ParticipatedBountyItem {
  id: string;
  title: string;
  reward_usdc: number;
  bounty_status: BountyStatus;
  bid_status: BidSelectionStatus;
  bidding_deadline: string | null;
  completion_deadline: string | null;
  review_round: number;
  created_at: string;
  bid_created_at: string;
}

interface UseParticipatedBountiesOptions {
  engineerId: string | null;
  page?: number;
  pageSize?: number;
}

export function useParticipatedBounties(options: UseParticipatedBountiesOptions) {
  const { engineerId, page = 0, pageSize = 10 } = options;

  return useQuery({
    queryKey: [...queryKeys.bounties.my(engineerId ?? ""), "participated", { page }],
    queryFn: async (): Promise<{ items: ParticipatedBountyItem[]; total: number }> => {
      if (!engineerId) return { items: [], total: 0 };

      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const from = page * pageSize;
      const to = from + pageSize - 1;

      // 通过 RPC 或 join 查询
      const { data, error, count } = await supabase
        .from("bounty_bids")
        .select(
          `
          bounty_id,
          status,
          created_at,
          bounty:bounties!inner(
            id, title, reward_usdc, status, bidding_deadline, completion_deadline, review_round, created_at
          )
        `,
          { count: "exact" }
        )
        .eq("engineer_id", engineerId)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) throw new Error(error.message);

      const items: ParticipatedBountyItem[] = (data ?? []).map((record: unknown) => {
        const r = record as {
          bounty_id: string;
          status: string;
          created_at: string;
          bounty: {
            id: string;
            title: string;
            reward_usdc: number;
            status: BountyStatus;
            bidding_deadline: string | null;
            completion_deadline: string | null;
            review_round: number;
            created_at: string;
          };
        };
        return {
          id: r.bounty.id,
          title: r.bounty.title,
          reward_usdc: r.bounty.reward_usdc,
          bounty_status: r.bounty.status,
          bid_status: r.status as BidSelectionStatus,
          bidding_deadline: r.bounty.bidding_deadline,
          completion_deadline: r.bounty.completion_deadline,
          review_round: r.bounty.review_round,
          created_at: r.bounty.created_at,
          bid_created_at: r.created_at,
        };
      });

      return { items, total: count ?? 0 };
    },
    enabled: !!engineerId,
    staleTime: 30 * 1000,
  });
}

// ========== 任务详情（含四阶段信息） ==========

export interface BountyDetailV2 extends Bounty {
  publisher_nickname?: string | null;
  selected_engineer_nickname?: string | null;
  my_bid_status?: BidSelectionStatus | null;
}

export function useBountyDetailV2(bountyId: string | undefined, userId?: string | null) {
  return useQuery({
    queryKey: [...queryKeys.bounties.detail(bountyId ?? ""), "v2", userId ?? ""],
    queryFn: async (): Promise<BountyDetailV2 | null> => {
      if (!bountyId) return null;

      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      // 获取任务详情
      const { data: bounty, error } = await supabase
        .from("bounties")
        .select("*")
        .eq("id", bountyId)
        .single();

      if (error || !bounty) throw new Error(error?.message ?? "任务不存在");

      const result: BountyDetailV2 = { ...bounty };

      // 获取发布者昵称
      if (bounty.publisher_id) {
        const { data: publisher } = await supabase
          .from("users")
          .select("nickname")
          .eq("id", bounty.publisher_id)
          .single();
        result.publisher_nickname = publisher?.nickname ?? null;
      }

      // 获取中标者昵称（如果有）
      if (bounty.status === "in_progress" || bounty.status === "reviewing") {
        const { data: selectedBid } = await supabase
          .from("bounty_bids")
          .select("engineer_id, engineer:users!bounty_bids_engineer_id_fkey(nickname)")
          .eq("bounty_id", bountyId)
          .eq("status", "selected")
          .single();

        if (selectedBid) {
          const sb = selectedBid as unknown as {
            engineer_id: string;
            engineer: { nickname: string } | null;
          };
          result.selected_engineer_nickname = sb.engineer?.nickname ?? null;
        }
      }

      // 获取当前用户的投标状态
      if (userId) {
        const { data: myBid } = await supabase
          .from("bounty_bids")
          .select("status")
          .eq("bounty_id", bountyId)
          .eq("engineer_id", userId)
          .maybeSingle();

        if (myBid) {
          result.my_bid_status = myBid.status as BidSelectionStatus;
        }
      }

      return result;
    },
    enabled: !!bountyId,
    staleTime: 60 * 1000,
  });
}

// ========== 交付审查记录 ==========

export function useDeliveryReviews(bountyId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.bounties.detail(bountyId ?? ""), "delivery_reviews"],
    queryFn: async (): Promise<DeliveryReview[]> => {
      if (!bountyId) return [];

      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const { data, error } = await supabase
        .from("delivery_reviews")
        .select("*")
        .eq("bounty_id", bountyId)
        .order("round_number", { ascending: true });

      if (error) throw new Error(error.message);

      return (data ?? []) as DeliveryReview[];
    },
    enabled: !!bountyId,
    staleTime: 60 * 1000,
  });
}

// ========== Mutation Hooks ==========

/** 投递方案（简化版） */
export function useSubmitBidV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      bountyId: string;
      proposal: string;
      estimatedDays: number;
      contactInfo: string;
      proposedReward: number;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const { data: userData } = await supabase.auth.getUser();
      const engineerId = userData.user?.id;
      if (!engineerId) throw new Error("请先登录");

      const { submitBid } = await import("@/lib/supabase/rpc");
      const result = await submitBid(
        supabase,
        input.bountyId,
        engineerId,
        input.proposal,
        "", // approach - 简化版不填
        input.estimatedDays,
        input.proposedReward,
        "", // relevantExperience - 简化版不填
        undefined,
        undefined,
        input.contactInfo
      );

      if (!result.success) {
        throw new Error(result.error ?? "投递失败");
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.bounties.detail(variables.bountyId), "v2"],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

/** 结束投递选标（发布者操作） */
export function useEndBiddingSelectWinner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bountyId,
      bidId,
    }: {
      bountyId: string;
      bidId: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const { data: userData } = await supabase.auth.getUser();
      const publisherId = userData.user?.id;
      if (!publisherId) throw new Error("请先登录");

      const { selectBidder } = await import("@/lib/supabase/rpc");
      const result = await selectBidder(supabase, bountyId, bidId, publisherId);

      if (!result.success) {
        throw new Error(result.error ?? "选标失败");
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

/** 提交交付物（审查期） */
export function useSubmitDeliveryReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bountyId,
      repoUrl,
      description,
    }: {
      bountyId: string;
      repoUrl: string;
      description: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const { data: userData } = await supabase.auth.getUser();
      const engineerId = userData.user?.id;
      if (!engineerId) throw new Error("请先登录");

      const { submitDeliveryReview } = await import("@/lib/supabase/rpc");
      const result = await submitDeliveryReview(supabase, bountyId, engineerId, {
        repo_url: repoUrl,
        message: description,
      });

      if (!result.success) {
        throw new Error(result.error ?? "提交失败");
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.bounties.detail(variables.bountyId), "delivery_reviews"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
    },
  });
}

/** 审核交付物（通过/驳回） */
export function useReviewDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deliveryReviewId,
      status,
      feedback,
    }: {
      deliveryReviewId: string;
      status: "approved" | "rejected";
      feedback?: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const { data: userData } = await supabase.auth.getUser();
      const publisherId = userData.user?.id;
      if (!publisherId) throw new Error("请先登录");

      const { reviewDelivery } = await import("@/lib/supabase/rpc");
      const result = await reviewDelivery(supabase, deliveryReviewId, publisherId, status, feedback);

      if (!result.success) {
        throw new Error(result.error ?? "审核失败");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

// ========== 缓存工具 ==========

export function useBountyQueryUtilsV2() {
  const queryClient = useQueryClient();

  const invalidateHall = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
  }, [queryClient]);

  const invalidatePublished = useCallback(
    (publisherId: string) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.bounties.my(publisherId), "published"],
      });
    },
    [queryClient]
  );

  const invalidateParticipated = useCallback(
    (engineerId: string) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.bounties.my(engineerId), "participated"],
      });
    },
    [queryClient]
  );

  const invalidateDetail = useCallback(
    (bountyId: string) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(bountyId),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.bounties.detail(bountyId), "v2"],
      });
    },
    [queryClient]
  );

  return {
    invalidateHall,
    invalidatePublished,
    invalidateParticipated,
    invalidateDetail,
  };
}

// ========== 辅助函数 ==========

/** 格式化赏金金额 */
export function formatReward(amount: number): string {
  return `¥${amount.toLocaleString("zh-CN")}`;
}

/** 格式化日期 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return "未设置";
  return new Date(dateStr).toLocaleDateString("zh-CN", {
    month: "short",
    day: "numeric",
  });
}

/** 格式化完整日期时间 */
export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "未设置";
  return new Date(dateStr).toLocaleString("zh-CN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** 计算剩余天数 */
export function getDaysRemaining(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const deadline = new Date(dateStr);
  const now = new Date();
  const diff = deadline.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/** 获取状态显示配置 */
export function getStatusConfig(status: BountyStatus) {
  const configs: Record<
    BountyStatus,
    { label: string; color: string; bgColor: string }
  > = {
    drafting: { label: "草稿", color: "#94A3B8", bgColor: "rgba(148, 163, 184, 0.15)" },
    bidding: { label: "投递期", color: "#6366F1", bgColor: "rgba(99, 102, 241, 0.15)" },
    in_progress: { label: "开发期", color: "#F59E0B", bgColor: "rgba(245, 158, 11, 0.15)" },
    reviewing: { label: "审查期", color: "#F97316", bgColor: "rgba(249, 115, 22, 0.15)" },
    completed: { label: "已完成", color: "#10B981", bgColor: "rgba(16, 185, 129, 0.15)" },
    cancelled: { label: "已取消", color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.15)" },
  };

  return configs[status] ?? configs.drafting;
}

/** 获取投标状态显示配置 */
export function getBidStatusConfig(status: BidSelectionStatus) {
  const configs: Record<BidSelectionStatus, { label: string; color: string }> = {
    pending: { label: "待审核", color: "#F59E0B" },
    selected: { label: "已中标", color: "#10B981" },
    not_selected: { label: "未中标", color: "#94A3B8" },
  };

  return configs[status] ?? { label: "未知", color: "#94A3B8" };
}
